import { Link } from "react-router-dom";

const GREEN = "#0F4D32";
const ADDRESS = "7/9 Molade Okoya Thomas, Victoria Island, Lagos, Nigeria";
const EMAIL = "info@epd-nigeria.com";
const UPDATED = "June 2026";

function LegalShell({ title, testid, children }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/get-started" className="font-display font-semibold tracking-tight text-slate-900">
            <span style={{ color: GREEN }}>EPD</span> Nigeria
          </Link>
          <Link to="/get-started" className="text-sm text-slate-500 hover:text-slate-900">← Back to site</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12" data-testid={testid}>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: {UPDATED}</p>
        <div className="mt-8 space-y-6 text-sm text-slate-700 leading-relaxed">{children}</div>
        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500">
          EPD Nigeria · {ADDRESS} · <a href={`mailto:${EMAIL}`} className="underline">{EMAIL}</a>
          <div className="mt-3 flex gap-4">
            <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms of Use</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

function H({ children }) {
  return <h2 className="font-display text-lg font-semibold text-slate-900 mt-2">{children}</h2>;
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" testid="privacy-page">
      <p>
        This Privacy Policy explains how EPD Nigeria (“EPD Nigeria”, “we”, “us”) handles information when you use our
        enterprise Environmental Product Declaration (EPD) platform. By using the platform you agree to the practices
        described here.
      </p>
      <H>Information we collect</H>
      <p>
        We collect information you provide when you register and use the platform, including your name, work email,
        organisation details, and the product, plant and production data you enter to prepare EPDs. We also collect
        limited technical data (such as log and session information) needed to operate and secure the service.
      </p>
      <H>How we use information</H>
      <p>
        We use your information to provide the platform, generate EPD documents and life-cycle calculations, manage your
        subscription and billing, communicate service and account notifications, and maintain the security and integrity
        of the service. We do not sell your personal information.
      </p>
      <H>Data you upload</H>
      <p>
        Product, plant and production data you enter remains associated with your organisation’s account and is accessed
        on a role-based basis. Information published on a public declaration record is limited to the declaration details
        you choose to issue.
      </p>
      <H>Payments</H>
      <p>
        Subscription payments are processed by our third-party payment provider. We do not store full card details on our
        servers; payment card data is handled by the payment provider under its own terms and security controls.
      </p>
      <H>Sharing</H>
      <p>
        We share information only with service providers that help us operate the platform (for example payment,
        email and hosting providers), or where required by law. These providers process data on our behalf under
        appropriate confidentiality obligations.
      </p>
      <H>Data retention</H>
      <p>
        We retain account and declaration records for as long as your account is active and as needed to provide the
        service, meet legal or contractual obligations, and maintain the audit trail associated with issued declarations.
      </p>
      <H>Your choices</H>
      <p>
        You may request access to, correction of, or deletion of your personal information, subject to records we are
        required to retain. To make a request, contact us at the address below.
      </p>
      <H>Contact</H>
      <p>
        Questions about this Policy can be sent to <a href={`mailto:${EMAIL}`} className="underline" style={{ color: GREEN }}>{EMAIL}</a>,
        or by post to EPD Nigeria, {ADDRESS}.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Use" testid="terms-page">
      <p>
        These Terms of Use govern access to and use of the EPD Nigeria platform. By registering for or using the
        platform, the customer organisation and its authorised users agree to these Terms.
      </p>
      <H>The service</H>
      <p>
        EPD Nigeria provides a software platform for collecting product and plant data, performing deterministic
        life-cycle assessment calculations aligned with recognised EPD frameworks, running automated technical
        validation, and producing EPD documents with a public declaration record and QR code.
      </p>
      <H>Role of EPD Nigeria</H>
      <p>
        EPD Nigeria provides the platform and automated technical validation. EPD Nigeria is not itself an independent
        verifier, accreditation body, government registry, or official EPD programme operator. Independent verification
        and any applicable third-party programme registration are performed by external parties and are separate from
        your platform subscription.
      </p>
      <H>Accounts and access</H>
      <p>
        You are responsible for the accuracy of the data you enter, for maintaining the confidentiality of your login
        credentials, and for activity under your account. Access is licensed per named user as set out in your
        subscription.
      </p>
      <H>Subscriptions and payment</H>
      <p>
        Access is provided on a subscription basis. Fees, named-user counts and terms are those presented at the time of
        purchase. Subscriptions activate once payment is confirmed. Fees are stated exclusive of any independent
        verification or third-party programme registration costs.
      </p>
      <H>Customer data and outputs</H>
      <p>
        You retain ownership of the data you submit. You are responsible for ensuring you have the rights to submit that
        data and for the accuracy of declarations you choose to issue. EPD documents reflect the data and datasets in
        effect at the time of generation.
      </p>
      <H>Acceptable use</H>
      <p>
        You agree not to misuse the platform, attempt to circumvent access controls, or use it to publish false or
        misleading declarations.
      </p>
      <H>Disclaimer and liability</H>
      <p>
        The platform is provided on an “as is” basis. To the fullest extent permitted by law, EPD Nigeria is not liable
        for indirect or consequential loss. Nothing in these Terms excludes liability that cannot be excluded by law.
      </p>
      <H>Changes</H>
      <p>
        We may update these Terms from time to time. Continued use of the platform after an update constitutes
        acceptance of the revised Terms.
      </p>
      <H>Contact</H>
      <p>
        Questions about these Terms can be sent to <a href={`mailto:${EMAIL}`} className="underline" style={{ color: GREEN }}>{EMAIL}</a>,
        or by post to EPD Nigeria, {ADDRESS}.
      </p>
    </LegalShell>
  );
}
